import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Container, Button } from "react-bootstrap";
import { FaFacebook, FaInstagram, FaTelegram } from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import PartnerLoader from "./PartnerLoader";
import PaylaterMark from "@assets/img/paylater-mark.png";
import PaylaterBanner from "@assets/img/paylater-banner.jpg";
import { trackEvent } from "@/utils/analytics";
import "./Partners.scss";
import { useGlobalState } from "../../context/GlobalStateContext";
import { getPartnerById } from "../../services/api";

function formatUzbekPhone(phone) {
  const digits = phone.replace(/\D/g, "").slice(-9);
  if (digits.length < 9) return phone;

  const parts = [
    digits.slice(0, 2),
    digits.slice(2, 5),
    digits.slice(5, 7),
    digits.slice(7, 9),
  ];

  return `+998 ${parts[0]} ${parts[1]} ${parts[2]} ${parts[3]}`;
}

export default function PartnerDetails() {
  const { id } = useParams();
  const { t: tSiteVersion } = useTranslation(["common"]);
  const navigate = useNavigate();
  const { language } = useGlobalState();

  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchPartner = async () => {
      try {
        const data = await getPartnerById(id, language);
        setPartner({
          ...data,
          logoFile: data.logoFile
            ? `data:image/png;base64,${data.logoFile}`
            : null,
          backgroundFile: data.backgroundFile
            ? `data:image/png;base64,${data.backgroundFile}`
            : null,
        });
      } catch (err) {
        setPartner(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPartner();
  }, [id, language]);

  if (loading) {
    return (
      <Container className="my-5 partner-details">
        <PartnerLoader />
      </Container>
    );
  }

  if (!partner) {
    return <div className="p-4">{tSiteVersion("partners.notFound")}</div>;
  }

  const {
    brandName,
    productCategory,
    phone,
    telegram,
    dsc,
    logoFile,
    backgroundFile,
    facebook,
    instagram,
    url,
  } = partner;

  return (
    <Container className="mt-4 partner-details">
      <div className="back-nav mb-4 text-muted">
        <Button
          variant="light"
          className="back-button"
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate("/", { replace: true });
            }
          }}
        >
          <IoArrowBack />
        </Button>
        <div>
          <Link to="/" className="text-muted text-decoration-none">
            {productCategory}
          </Link>{" "}
          / <span className="ms-1">{brandName}</span>
        </div>
      </div>
      <div className="header-image-wrapper">
        <img
          src={backgroundFile || PaylaterBanner}
          alt="Room"
          className="header-image"
        />

        <div className="header-overlay">
          <div>
            <img src={logoFile || PaylaterMark} alt="Logo" className="logo" />
            <h5 className="mb-1">{brandName}</h5>
            <span className="badge bg-danger mb-3">{productCategory}</span>
          </div>

          <div className="contacts">
            {phone && (
              <>
                <p className="mb-0">Call Center:</p>
                <p className="mb-3">
                  <a href={`tel:${phone}`} className="text-decoration-none">
                    {formatUzbekPhone(phone)}
                  </a>
                </p>
              </>
            )}

            {(telegram || instagram || facebook) && (
              <p className="mb-1">{tSiteVersion("partners.socialMedia")}</p>
            )}

            {telegram && (
              <a
                href={`https://t.me/${telegram.replace("@", "").trim()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="fs-5 me-2"
              >
                <FaTelegram size={32} />
              </a>
            )}
            {instagram && (
              <a
                href={`https://instagram.com/${instagram
                  .replace("@", "")
                  .trim()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="fs-5 me-2"
              >
                <FaInstagram size={32} />
              </a>
            )}

            {facebook && (
              <a
                href={`https://facebook.com/${facebook
                  .replace("@", "")
                  .trim()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="fs-5 me-2"
              >
                <FaFacebook size={32} />
              </a>
            )}
          </div>
        </div>
      </div>
      <h5 className="mb-3">{tSiteVersion("partners.about")}</h5>
      <div className="description-block">
        {url && (
          <>
            <p className="mb-0">{tSiteVersion("partners.url")}</p>
            <p className="mb-3">
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-decoration-none"
                onClick={() =>
                  trackEvent("partner_link_outbound", {
                    partner_name: partner.brandName || "unknown",
                    partner_id: partner.id,
                    link: url,
                    source: "partner",
                  })
                }
              >
                {url}
              </a>
            </p>
          </>
        )}
        <p>{dsc}</p>
      </div>
    </Container>
  );
}
